import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCog,
  KeyRound,
  Phone,
  Mail,
  TrendingUp,
  FileText,
  Users as UsersIcon,
  MapPin,
  FileSignature,
  Upload,
  ExternalLink,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RUOLI = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'sub_agent', label: 'Sub-agente' },
  { value: 'interno', label: 'Utente Interno' },
  { value: 'back_office', label: 'Back Office' },
  { value: 'operatore_call', label: 'Operatore Call Center' },
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [reti, setReti] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editCompagnieIds, setEditCompagnieIds] = useState([]);
  const [savingCompagnie, setSavingCompagnie] = useState(false);
  const [newUser, setNewUser] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    password: '',
    ruolo: 'sub_agent',
    attivo: true,
    area_geografica: '',
    rete_vendita_id: ''
  });

  const { hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchReti();
    fetchCompanies();
  }, [filterRuolo]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies`);
      setCompanies(res.data || []);
    } catch (e) {}
  };

  const loadVisibleCompanies = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/users/${userId}/compagnie-visibili`);
      setEditCompagnieIds(res.data.compagnie_ids || []);
    } catch (e) {
      setEditCompagnieIds([]);
    }
  };

  const toggleEditCompagnia = (compId) => {
    setEditCompagnieIds(prev => prev.includes(compId) ? prev.filter(id => id !== compId) : [...prev, compId]);
  };

  const saveCompagnieVisibili = async () => {
    if (!selectedUser) return;
    setSavingCompagnie(true);
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}/compagnie-visibili`, { compagnie_ids: editCompagnieIds });
      toast.success(editCompagnieIds.length === 0 ? 'Visibilita: vede tutte le compagnie' : `Visibilita aggiornata: ${editCompagnieIds.length} compagnie`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento visibilita');
    } finally {
      setSavingCompagnie(false);
    }
  };

  const fetchReti = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reti-vendita`);
      setReti(res.data);
    } catch (e) {}
  };

  useEffect(() => {
    fetchUsers();
  }, [filterRuolo]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRuolo) params.append('ruolo', filterRuolo);

      const response = await axios.get(`${API_URL}/api/users?${params}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const payload = { ...newUser };
      if (!payload.rete_vendita_id) delete payload.rete_vendita_id;
      await axios.post(`${API_URL}/api/auth/register`, payload);
      toast.success('Utente creato con successo');
      setShowCreateModal(false);
      setNewUser({
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        password: '',
        ruolo: 'sub_agent',
        attivo: true,
        area_geografica: '',
        rete_vendita_id: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'utente');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, {
        nome: selectedUser.nome,
        cognome: selectedUser.cognome,
        telefono: selectedUser.telefono,
        ruolo: selectedUser.ruolo,
        attivo: selectedUser.attivo,
        area_geografica: selectedUser.area_geografica,
        rete_vendita_id: selectedUser.rete_vendita_id || null
      });
      toast.success('Utente aggiornato');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      toast.success('Utente eliminato');
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'eliminazione dell\'utente');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}/reset-password`, { password: newPassword });
      toast.success(`Password aggiornata per ${selectedUser.nome} ${selectedUser.cognome}`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset della password');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API_URL}/api/users/${user.id}`, {
        attivo: !user.attivo
      });
      toast.success(user.attivo ? 'Utente disattivato' : 'Utente attivato');
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleUploadPiano = async (user, file) => {
    if (!file) return;
    const okExt = /\.(pdf|jpg|jpeg|png|webp)$/i.test(file.name);
    if (!okExt) { toast.error('Formato non supportato: PDF, JPG, PNG'); return; }
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API_URL}/api/users/${user.id}/piano-provvigionale`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Piano provvigionale caricato');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
  };

  const getRuoloLabel = (ruolo) => {
    return RUOLI.find(r => r.value === ruolo)?.label || ruolo;
  };

  const getRuoloColor = (ruolo) => {
    const colors = {
      super_admin: 'bg-violet-100 text-violet-700',
      interno: 'bg-orange-100 text-orange-700',
      sub_agent: 'bg-emerald-100 text-emerald-700',
      back_office: 'bg-slate-100 text-slate-700',
      operatore_call: 'bg-blue-100 text-blue-700'
    };
    return colors[ruolo] || 'bg-slate-100 text-slate-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.nome?.toLowerCase().includes(searchLower) ||
      user.cognome?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Utenti</h1>
          <p className="text-slate-500 mt-1">{users.length} utenti totali</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Utente
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome, cognome, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
            <Select value={filterRuolo || "all"} onValueChange={(v) => setFilterRuolo(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tutti i ruoli" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i ruoli</SelectItem>
                {RUOLI.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-4 py-3">Utente</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3">Ruolo</th>
                  <th className="text-left px-4 py-3">Creato da</th>
                  <th className="text-left px-4 py-3">Rete</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Data Creazione</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      Nessun utente trovato
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr 
                      key={user.id} 
                      className="table-row"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-medium">
                            {user.nome?.[0]}{user.cognome?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {user.nome} {user.cognome}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                          {user.telefono && (
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Phone className="w-3 h-3" />
                              {user.telefono}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getRuoloColor(user.ruolo)}`}>
                          {getRuoloLabel(user.ruolo)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.parent_nome ? (
                          <span className="text-sm text-slate-600">{user.parent_nome}</span>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.rete_vendita_nome ? (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <MapPin className="w-3 h-3" />
                            {user.rete_vendita_nome}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          user.attivo ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.attivo ? 'Attivo' : 'Disattivato'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedUser(user);
                              loadVisibleCompanies(user.id);
                              setShowEditModal(true);
                            }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                              <UserCog className="w-4 h-4 mr-2" />
                              {user.attivo ? 'Disattiva' : 'Attiva'}
                            </DropdownMenuItem>
                            {hasRole(['super_admin', 'back_office', 'interno']) && (
                              <DropdownMenuItem asChild>
                                <label className="cursor-pointer">
                                  <FileSignature className="w-4 h-4 mr-2" />
                                  {user.piano_provvigionale_url ? 'Sostituisci piano provv.' : 'Carica piano provv.'}
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                    className="hidden"
                                    onChange={(e) => handleUploadPiano(user, e.target.files?.[0])}
                                  />
                                </label>
                              </DropdownMenuItem>
                            )}
                            {user.piano_provvigionale_url && (
                              <DropdownMenuItem onClick={() => window.open(`${API_URL}${user.piano_provvigionale_url}`, '_blank')}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Apri piano provvigionale
                              </DropdownMenuItem>
                            )}
                            {hasRole(['super_admin', 'sub_agent']) && (
                              <>
                                <DropdownMenuItem onClick={() => {
                                  setSelectedUser(user);
                                  setNewPassword('');
                                  setShowPasswordModal(true);
                                }}>
                                  <KeyRound className="w-4 h-4 mr-2" />
                                  Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo Utente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={newUser.nome}
                  onChange={(e) => setNewUser({...newUser, nome: e.target.value})}
                  placeholder="Mario"
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome *</Label>
                <Input
                  value={newUser.cognome}
                  onChange={(e) => setNewUser({...newUser, cognome: e.target.value})}
                  placeholder="Rossi"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="mario.rossi@email.it"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={newUser.telefono}
                onChange={(e) => setNewUser({...newUser, telefono: e.target.value})}
                placeholder="+39 333 1234567"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select 
                value={newUser.ruolo} 
                onValueChange={(v) => setNewUser({...newUser, ruolo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RUOLI.filter(r => {
                    if (hasRole(['super_admin'])) return true;
                    if (hasRole(['sub_agent'])) return ['sub_agent', 'interno', 'back_office', 'operatore_call'].includes(r.value);
                    return false;
                  }).map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Area Geografica</Label>
              <Input
                value={newUser.area_geografica}
                onChange={(e) => setNewUser({...newUser, area_geografica: e.target.value})}
                placeholder="es. Milano e provincia, Lombardia Nord"
              />
              <p className="text-xs text-slate-500">Zona di competenza del sub-agente</p>
            </div>
            {hasRole(['super_admin']) && reti.length > 0 && (
              <div className="space-y-2">
                <Label>Rete di Vendita</Label>
                <Select 
                  value={newUser.rete_vendita_id || "none"} 
                  onValueChange={(v) => setNewUser({...newUser, rete_vendita_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger data-testid="create-user-rete-select">
                    <SelectValue placeholder="Seleziona rete" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna rete</SelectItem>
                    {reti.filter(r => r.attiva).map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={!newUser.nome || !newUser.cognome || !newUser.email || !newUser.password}
              data-testid="submit-new-user-btn"
            >
              Crea Utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={selectedUser.nome}
                    onChange={(e) => setSelectedUser({...selectedUser, nome: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome</Label>
                  <Input
                    value={selectedUser.cognome}
                    onChange={(e) => setSelectedUser({...selectedUser, cognome: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={selectedUser.email}
                  disabled
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={selectedUser.telefono || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, telefono: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select 
                  value={selectedUser.ruolo} 
                  onValueChange={(v) => setSelectedUser({...selectedUser, ruolo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RUOLI.filter(r => {
                      if (hasRole(['super_admin'])) return true;
                      if (hasRole(['sub_agent'])) return ['sub_agent', 'interno', 'back_office', 'operatore_call'].includes(r.value);
                      return false;
                    }).map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Area Geografica</Label>
                <Input
                  value={selectedUser.area_geografica || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, area_geografica: e.target.value})}
                  placeholder="es. Milano e provincia"
                />
              </div>
              {hasRole(['super_admin']) && reti.length > 0 && (
                <div className="space-y-2">
                  <Label>Rete di Vendita</Label>
                  <Select 
                    value={selectedUser.rete_vendita_id || "none"} 
                    onValueChange={(v) => setSelectedUser({...selectedUser, rete_vendita_id: v === "none" ? null : v})}
                  >
                    <SelectTrigger data-testid="edit-user-rete-select">
                      <SelectValue placeholder="Seleziona rete" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna rete</SelectItem>
                      {reti.filter(r => r.attiva).map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Compagnie visibili */}
              {hasRole(['super_admin']) && companies.length > 0 && selectedUser.ruolo !== 'super_admin' && (
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label>Compagnie Visibili a questo Utente</Label>
                    <div className="flex gap-2 text-xs">
                      <button type="button" onClick={() => setEditCompagnieIds(companies.map(c => c.id))} className="text-sky-600 hover:underline">Tutte</button>
                      <span className="text-slate-300">|</span>
                      <button type="button" onClick={() => setEditCompagnieIds([])} className="text-slate-500 hover:underline">Nessun limite</button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {editCompagnieIds.length === 0
                      ? 'Nessun limite: vede e usa tutte le compagnie del CRM'
                      : `Limitato: vede solo le ${editCompagnieIds.length} compagnie selezionate`}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto p-2 border rounded-md bg-slate-50/30">
                    {companies.map(comp => {
                      const checked = editCompagnieIds.includes(comp.id);
                      return (
                        <label key={comp.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm">
                          <input type="checkbox" checked={checked} onChange={() => toggleEditCompagnia(comp.id)} className="rounded" />
                          <span className="truncate" title={comp.nome}>{comp.nome}</span>
                        </label>
                      );
                    })}
                  </div>
                  <Button variant="outline" size="sm" onClick={saveCompagnieVisibili} disabled={savingCompagnie} data-testid="save-compagnie-visibili-btn">
                    {savingCompagnie ? 'Salvataggio...' : 'Salva Visibilita Compagnie'}
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateUser}>
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedUser.nome} {selectedUser.cognome}</p>
                <p className="text-sm text-slate-500">{selectedUser.email}</p>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${getRuoloColor(selectedUser.ruolo)}`}>
                  {getRuoloLabel(selectedUser.ruolo)}
                </span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  data-testid="reset-password-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Annulla</Button>
            <Button onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 6} data-testid="confirm-reset-password-btn">
              Imposta Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;